import React, { useEffect } from "react";
import { IconArrowRight, IconSettings } from "@tabler/icons-react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import Button from "components/Button";
import Heading from "components/Heading";
import SearchSortTable from "components/Table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import InventoryTableColumns from "./InventoryTableColumns";
import { inventoryStore } from "store/InventoryStore";
import { observer } from "mobx-react-lite";
import { useInventory } from "./useInventory";
import CustomizeColumns from "./CustomizeColumns";
import { skeleton } from "utils";
import { buildingStore } from "store/Building/BuildingStore";
import EmptyState from "components/EmptyState";
import { ROUTES_ENUM } from "routes/consts";

const DEFAULT_SORT = [
  {
    id: "object_name",
    desc: false,
  },
];

const BuildingInventory = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sourceFiles } = buildingStore;
  const [initialSetupLoading, setInitialSetupLoading] = React.useState(true);
  const [openSettings, setOpenSettings] = React.useState(false);
  const { currentBuilding } = buildingStore;
  const { getColumnsAndStates, getColumnsVisibility, getDebugColumns } =
    useInventory();

  const fetchData = async () => {
    if (currentBuilding?.id && initialSetupLoading) {
      inventoryStore.setBuildingId(currentBuilding?.id);
      await getColumnsAndStates();
      await inventoryStore.fetchColumnData(inventoryStore.columnFilters);
      setInitialSetupLoading(false);
    }
  };

  useEffect(() => {
    getDebugColumns();
  }, []);

  useEffect(() => {
    (async () => await fetchData())();
  }, [currentBuilding?.id]);

  useEffect(() => {
    !initialSetupLoading && getColumnsAndStates();
  }, [currentBuilding?.conformity_id]);

  useEffect(() => {
    if (initialSetupLoading) return;
    (async () => {
      inventoryStore.setLoadingMore(true);
      await inventoryStore.fetchColumnData(inventoryStore.columnFilters);
      inventoryStore.setLoadingMore(false);
    })();
  }, [inventoryStore.columnFilters, inventoryStore.sorting]);

  const handleSortChanged = (e: SortingState) => inventoryStore.setSorting(e);

  const fetchLoadMoreData = async (start: number) => {
    if (inventoryStore.loadingMore) return;
    inventoryStore.setOffset(start);
    await inventoryStore.loadMoreData(inventoryStore.columnFilters);
  };

  function buttons() {
    if (initialSetupLoading || inventoryStore.filterLoading) return <></>;
    return (
      <div className="flex">
        <Button
          type="secondary"
          leadingIcon={<IconSettings width={20} />}
          width="fit-content"
          className="ml-4"
          onClick={() => setOpenSettings(true)}
          testID="customizecolumns_button"
          disable={buildingStore.calculationsStatus.inProgress}
        >
          {t("buildingInventory.customizeColumns")}
        </Button>
        <Button
          type="primary"
          trailingIcon={<IconArrowRight width={20} />}
          width="fit-content"
          className="ml-4"
          onClick={() => navigate(`../${ROUTES_ENUM.SOURCE_FILES}/`)}
          testID="manageinventory_button"
        >
          {t("buildingInventory.manageInventory")}
        </Button>
      </div>
    );
  }

  function content() {
    if (!buildingStore.buildingMappingStatus.hasReport)
      return (
        <EmptyState
          type="inventory"
          loading={buildingStore.calculationsStatus.inProgress}
        />
      );

    return (
      <div>
        <div className="md:flex justify-between mb-3">
          <div>
            <Heading size="title">{t("buildingInventory.inventory")}</Heading>
            <Heading size="subtitle">{t("buildingInventory.subtitle")}</Heading>
          </div>
          {buttons()}
        </div>
        {buildingStore.calculationsStatus.inProgress ? (
          <EmptyState
            type="inventory"
            loading={
              buildingStore.calculationsStatus.inProgress ||
              inventoryStore.filterLoading
            }
          />
        ) : (
          <SearchSortTable
            getColumns={
              InventoryTableColumns as () => ColumnDef<unknown, unknown>[]
            }
            enableColumnResizing
            borderedStyle
            data={inventoryStore.data}
            hasGroupHeader
            totalDBRowCount={inventoryStore.totalCount}
            dataFetchLoading={inventoryStore.loadingMore}
            fetchData={fetchLoadMoreData}
            scrollDivClassName="scrollbar"
            groupHeaderClassName="py-1 text-xs"
            zebraStriped
            hiddenColumns={getColumnsVisibility()}
            fetchSize={inventoryStore.limit}
            onSortChanged={handleSortChanged}
            enableTooltip
            defaultSort={DEFAULT_SORT}
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      {sourceFiles.loading || initialSetupLoading
        ? skeleton("h-80")
        : content()}

      <CustomizeColumns
        open={openSettings}
        close={() => setOpenSettings(false)}
      />
    </div>
  );
});

export default BuildingInventory;
